import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col, Form, Input, message, Popconfirm, Switch } from 'antd';
import {  
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  UserOutlined,
  SendOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from './EditBoxModal';

const TelegramUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize] = useState(15);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const [isDirectModalVisible, setIsDirectModalVisible] = useState(false);
  const [directTarget, setDirectTarget] = useState(null);
  const [directMessage, setDirectMessage] = useState('');
  const [directSending, setDirectSending] = useState(false);

  const [form] = Form.useForm();
  const CARD_PADDING = '10px 20px'; 
  const USERS_URL = '/api/box/telegram-users/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(USERS_URL);
      setUsers(res.data);
    } catch (error) {
      message.error('Error loading Telegram users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) return message.warning('Unesi poruku');
    setSending(true);
    try {
      await axios.post(`${USERS_URL}send_message/`, { message: broadcastMessage });
      message.success('Broadcast poslat!');
      setBroadcastMessage('');
    } catch (error) {
      message.error('Greška pri slanju');
    } finally {
      setSending(false);
    }
  };

  const openDirectModal = (record) => {
    setDirectTarget(record);
    setDirectMessage('');
    setIsDirectModalVisible(true);
  };

  const handleDirectSend = async () => {
    if (!directMessage.trim()) return message.warning('Unesi poruku');
    setDirectSending(true);
    try {
      await axios.post(`${USERS_URL}${directTarget.id}/send_direct_message/`, { message: directMessage });
      message.success(`Poslato korisniku ${directTarget.ime}`);
      setIsDirectModalVisible(false);
    } catch (error) {
      message.error('Slanje neuspešno');
    } finally {
      setDirectSending(false);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editingUser) {
        await axios.put(`${USERS_URL}${editingUser.id}/`, values);
        message.success('Korisnik ažuriran');
      } else {
        await axios.post(USERS_URL, values);
        message.success('Korisnik dodat');
      }
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('Greška pri čuvanju');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${USERS_URL}${id}/`);
      message.success('Korisnik obrisan');
      fetchData();
    } catch (error) {
      message.error('Greška pri brisanju');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'ime',
      key: 'ime',
      render: (text) => <span style={{ color: 'white', fontWeight: '500' }}>{text}</span>,
    },
    {
      title: 'Chat ID',
      dataIndex: 'chat_id',
      key: 'chat_id',
      render: (text) => <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'aktivan',
      key: 'aktivan',
      render: (aktivan) => (
        <Switch checked={aktivan} disabled size="small" />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Space size="middle">
          <Button 
            size="small" 
            icon={<SendOutlined />} 
            onClick={() => openDirectModal(record)}
            style={{ background: '#065f46', color: '#34d399', border: '1px solid #064e3b' }} 
          />
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => { setEditingUser(record); form.setFieldsValue(record); setIsModalVisible(true); }}
            style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
          >Edit</Button>
          <Popconfirm title="Obriši korisnika?" onConfirm={() => handleDelete(record.id)}>
            <Button 
              size="small" 
              icon={<DeleteOutlined />} 
              style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
            >Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={6}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Space size="middle">
              <UserOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL USERS</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{users.length}</div>
              </div>
            </Space>
          </div>
        </Col>

        {/* SREDNJA KARTICA SA NASLOVOM */}
        <Col span={12}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #10b981', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', // Da naslov bude iznad
            justifyContent: 'center',
            alignItems: 'flex-start' 
          }}>
            <div style={{ color: '#10b981', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>
              Send Admin Message
            </div>
            <div style={{ display: 'flex', width: '100%' }}>
              <Input 
                placeholder="Send to all active users..." 
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                onPressEnter={handleBroadcast}
                style={{ flex: 1, marginRight: '10px', background: '#0f172a', color: 'white', border: '1px solid #334155' }}
              />
              <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleBroadcast} style={{ background: '#10b981', borderColor: '#10b981' }}>
                Send
              </Button>
            </div>
          </div>
        </Col>

        <Col span={6}>
          <div onClick={() => { setEditingUser(null); form.resetFields(); form.setFieldsValue({ aktivan: true }); setIsModalVisible(true); }} style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', cursor: 'pointer', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Space size="middle">
              <PlusOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>ADD NEW USER</div>
                <div style={{ color: 'white', fontSize: '14px' }}>Authorize bot access</div>
              </div>
            </Space>
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table dataSource={users} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize }} style={{ background: 'transparent' }} />
      </div>

      <EditBoxModal 
        title={editingUser ? "Edit Telegram User" : "Add Telegram User"} 
        isVisible={isModalVisible} 
        onOk={() => form.submit()} 
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="ime" label={<span style={{ color: '#94a3b8' }}>Name</span>} rules={[{ required: true }]}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
          <Form.Item name="chat_id" label={<span style={{ color: '#94a3b8' }}>Chat ID</span>} rules={[{ required: true }]}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
          <Form.Item name="aktivan" label={<span style={{ color: '#94a3b8' }}>Active</span>} valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </EditBoxModal>

      <EditBoxModal 
        title={`Send Message to ${directTarget?.ime}`} 
        isVisible={isDirectModalVisible} 
        onOk={handleDirectSend} 
        onCancel={() => setIsDirectModalVisible(false)}
        updating={directSending}
      >
        <Input.TextArea 
          rows={4} 
          placeholder="Unesi poruku..." 
          value={directMessage}
          onChange={(e) => setDirectMessage(e.target.value)}
          style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
        />
      </EditBoxModal>

      <style>{`
        .ant-table { background: transparent !important; color: #94a3b8 !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-pagination-item a { color: #94a3b8 !important; }
        .ant-pagination-item-active { background: #3b82f6 !important; border-color: #3b82f6 !important; }
        .ant-select-selector, .ant-input, .ant-input-textarea { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
      `}</style>
    </div>
  );
};

export default TelegramUsers;